import React from "react";

import { Context, templates } from "@reactivated";
import type { ServerCmsTypesFilterFacet as FilterFacet } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { CategoryContentBlock } from "../components/CategoryContentBlock";
import { getCategorySEO } from "../components/CategoryContentBlock/categorySEO";
import type { SelectedFilters } from "../components/CategoryContentBlock/filterProducts";
import {
    firstBlockOverlapsNav,
    isOverlapBlockType,
} from "../components/HeaderNav/overlap";
import { PageSkeleton } from "../components/PageSkeleton";
import { toolboxComponents } from "../components/PageSkeleton/Toolbox";

export const parseFiltersFromURL = (
    url: string,
    filterFacets: FilterFacet[],
): SelectedFilters => {
    try {
        const params = new URL(url).searchParams;
        const validValues = new Map(
            filterFacets.map((facet) => [
                facet.slug,
                new Set(facet.values.map((v) => v.slug)),
            ]),
        );
        const filters: SelectedFilters = {};
        for (const [key, value] of params.entries()) {
            if (!validValues.get(key)?.has(value)) continue;
            if (!filters[key]) filters[key] = [];
            filters[key].push(value);
        }
        return filters;
    } catch {
        return {};
    }
};

export const Template = (props: templates.CategoryPageTemplate) => {
    const context = React.useContext(Context);
    const rootTitle = context.root_page.seo_title || context.root_page.title;

    const [selectedFilters, setSelectedFilters] =
        React.useState<SelectedFilters>(() =>
            parseFiltersFromURL(context.request.url, props.page.filter_facets),
        );

    const categoryTitle = props.page.seo_title || props.page.title;

    const seoOverrides = getCategorySEO({
        filterFacets: props.page.filter_facets,
        selectedFilters,
        categoryTitle,
        requestUrl: context.request.url,
        rootTitle,
    });

    return (
        <PageSkeleton
            {...props.page}
            {...seoOverrides}
            overlapNav={firstBlockOverlapsNav(
                props.page.body.value.slice(0, 1).find(isOverlapBlockType),
            )}
        >
            <StreamField
                tagName={React.Fragment}
                value={props.page.body.value}
                components={{
                    ...toolboxComponents,
                    page_children: () => null,
                    category_content: () => (
                        <CategoryContentBlock
                            categoryTitle={categoryTitle}
                            categoryNavigation={props.page.category_navigation}
                            filterFacets={props.page.filter_facets}
                            products={props.page.products}
                            gridPromos={props.page.grid_promos}
                            facetValueGridPromos={
                                props.page.facet_value_grid_promos
                            }
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                        />
                    ),
                }}
            />
        </PageSkeleton>
    );
};
